import type { RecordingSource } from '../../visual/index.js';

/**
 * The playfield and the song, as one stream a recorder can take.
 *
 * ## The audio branch is a branch
 *
 * This is the part of the phase with a real hazard in it. The obvious way to
 * capture what the player hears is to route the master gain into a
 * `MediaStreamAudioDestinationNode` — and the obvious *mistake* is to move it
 * there, or to disconnect and reconnect it, or to insert a node in front of it.
 * Any of those changes what comes out of the speakers: a rerouted graph plays
 * at a different level, an inserted node adds latency, and a disconnect drops
 * audio for however long the rewiring takes.
 *
 * `connect` on a WebAudio node is additive. Calling `master.connect(recorder)`
 * leaves `master.connect(destination)` exactly as it was and sends the same
 * signal to both. Nothing is removed, no gain is touched, and the path to the
 * speakers is not altered by so much as a node — which is what §7 requires and
 * why `stop()` below disconnects *only* the branch it added.
 *
 * That also means recording cannot move the music clock. `MusicClock` reads
 * `AudioContext.currentTime`, and a side branch does not change it.
 *
 * ## The canvas
 *
 * `captureStream(fps)` on the renderer's own canvas — the one the game is
 * already drawing into, not a copy. The browser samples it on its own schedule;
 * the render loop is not asked to do anything differently, and nothing here runs
 * per frame.
 *
 * The HUD is DOM — panels, score, judgement — and sits outside the canvas, so a
 * canvas capture cannot include it. Compositing DOM into video would mean an
 * entirely new capture path, which §28 rules out. Canvas only, stated plainly in
 * the UI rather than silently.
 */

export interface CanvasAudioStreamOptions {
  readonly canvas: HTMLCanvasElement;
  readonly fps: number;
  /** The node carrying everything the player hears. Never modified. */
  readonly audioSource?: AudioNode | null;
  readonly audioContext?: AudioContext | null;
}

export type StreamFailure = 'no-capture' | 'no-audio-stream';

export type CanvasAudioStreamResult =
  | { readonly ok: true; readonly stream: CanvasAudioStream }
  | { readonly ok: false; readonly reason: StreamFailure; readonly message: string };

export class CanvasAudioStream implements RecordingSource {
  readonly mediaStream: MediaStream;
  readonly hasVideo: boolean;
  readonly hasAudio: boolean;
  readonly width: number;
  readonly height: number;
  readonly fps: number;

  private readonly branchSource: AudioNode | null;
  private readonly branchTarget: MediaStreamAudioDestinationNode | null;
  private released = false;

  private constructor(options: {
    stream: MediaStream;
    hasAudio: boolean;
    width: number;
    height: number;
    fps: number;
    branchSource: AudioNode | null;
    branchTarget: MediaStreamAudioDestinationNode | null;
  }) {
    this.mediaStream = options.stream;
    this.hasVideo = options.stream.getVideoTracks().length > 0;
    this.hasAudio = options.hasAudio;
    this.width = options.width;
    this.height = options.height;
    this.fps = options.fps;
    this.branchSource = options.branchSource;
    this.branchTarget = options.branchTarget;
  }

  /**
   * Builds the stream, or explains what was missing.
   *
   * Audio failing is not fatal to the whole recording — a silent capture is
   * better than none — but it is reported rather than swallowed, so the caller
   * can decide and the user is told which they got.
   */
  static create(options: CanvasAudioStreamOptions): CanvasAudioStreamResult {
    const canvas = options.canvas;
    if (typeof canvas.captureStream !== 'function') {
      return {
        ok: false,
        reason: 'no-capture',
        message: 'canvas.captureStream is unavailable in this browser',
      };
    }

    let stream: MediaStream;
    try {
      stream = canvas.captureStream(options.fps);
    } catch (cause) {
      return {
        ok: false,
        reason: 'no-capture',
        message: cause instanceof Error ? cause.message : String(cause),
      };
    }

    let branchSource: AudioNode | null = null;
    let branchTarget: MediaStreamAudioDestinationNode | null = null;
    let hasAudio = false;

    const context = options.audioContext ?? null;
    const source = options.audioSource ?? null;
    if (context !== null && source !== null) {
      try {
        branchTarget = context.createMediaStreamDestination();
        // Additive. The existing path to the speakers is untouched — see the
        // note at the top of this file.
        source.connect(branchTarget);
        branchSource = source;
        for (const track of branchTarget.stream.getAudioTracks()) {
          stream.addTrack(track);
          hasAudio = true;
        }
      } catch (cause) {
        // Undo whatever half-connected, then carry on without sound.
        try {
          if (branchSource !== null && branchTarget !== null) {
            branchSource.disconnect(branchTarget);
          }
        } catch {
          // Nothing was connected.
        }
        branchSource = null;
        branchTarget = null;
        hasAudio = false;
        for (const track of stream.getTracks()) track.stop();
        return {
          ok: false,
          reason: 'no-audio-stream',
          message: cause instanceof Error ? cause.message : String(cause),
        };
      }
    }

    const settings = stream.getVideoTracks()[0]?.getSettings?.() ?? {};
    return {
      ok: true,
      stream: new CanvasAudioStream({
        stream,
        hasAudio,
        width: (settings.width as number | undefined) ?? canvas.width,
        height: (settings.height as number | undefined) ?? canvas.height,
        fps: options.fps,
        branchSource,
        branchTarget,
      }),
    };
  }

  /**
   * Stops the capture and removes the audio branch.
   *
   * Only the branch is disconnected — `disconnect(target)` with an argument
   * removes that one connection and leaves every other output of the node
   * alone. A bare `disconnect()` here would cut the music off from the
   * speakers, which is the failure this method exists to avoid.
   */
  release(): void {
    if (this.released) return;
    this.released = true;

    for (const track of this.mediaStream.getTracks()) {
      try {
        track.stop();
      } catch {
        // Already ended.
      }
    }

    if (this.branchSource !== null && this.branchTarget !== null) {
      try {
        this.branchSource.disconnect(this.branchTarget);
      } catch {
        // Already disconnected, or the context went away with the page.
      }
    }
  }

  /** For diagnostics: what the stream is actually carrying. */
  describe(): {
    readonly video: number;
    readonly audio: number;
    readonly width: number;
    readonly height: number;
    readonly fps: number;
    readonly released: boolean;
  } {
    return {
      video: this.mediaStream.getVideoTracks().length,
      audio: this.mediaStream.getAudioTracks().length,
      width: this.width,
      height: this.height,
      fps: this.fps,
      released: this.released,
    };
  }
}
